import NumberFlow from '@number-flow/react';
import type { ColumnState } from 'ag-grid-community';
import type { CustomCellRendererProps } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { scoreTranslation } from '@treams/treams-analytics/src/score/services/ScoreConfig';
import { DataCard, Delta, Ellipsis, GaugeMeter, defaultTheme, tokens } from '@treams/treams-ui';

import { FAILED_SCORE, ScoreModuleState, isFailedScore } from '../TreamsScore.types';
import type { ScoreCategory, TreamsScoreRow } from '../TreamsScore.types';
import { toDisplayScore } from '../scoreDisplay';
import { TreamsScoreManagers } from './TreamsScoreManagers';

export const TOP_DIVIDER_ID = '__top_divider';
export const BOTTOM_DIVIDER_ID = '__bottom_divider';
export const HIDDEN_DIVIDER_ID = '__hidden_divider';

export const getEdgeCount = (teamCount: number): 0 | 3 | 5 | 10 => {
	if (teamCount < 7) return 0;
	if (teamCount <= 10) return 3;
	if (teamCount <= 20) return 5;
	return 10;
};

export type DividerRow = { id: string; divider: true };
export type GridRow = TreamsScoreRow | DividerRow;
export type ScoreColumnId = 'total' | ScoreCategory;
export type ScoreSort = { direction: 'asc' | 'desc'; label: string };
export type ActiveScoreSort = { colId: ScoreColumnId; direction: 'asc' | 'desc' };
export type ScoreColors = { background: string; color: string };

// Picks the edgeCount highest- and lowest-ranked teams on the sorted score
// column. Direction doesn't matter for the selection: both edges are kept and
// the grid orders them itself.
export const selectCompactTeams = (
	teams: TreamsScoreRow[],
	edgeCount: number,
	sort: ActiveScoreSort,
): TreamsScoreRow[] => {
	if (edgeCount <= 0 || teams.length <= edgeCount * 2) return [...teams];

	const ranked = [...teams].sort((a, b) => b.scores[sort.colId].score - a.scores[sort.colId].score);

	return [...ranked.slice(0, edgeCount), ...ranked.slice(-edgeCount)];
};

export const isDivider = (row?: GridRow | null): row is DividerRow => !!row && 'divider' in row;

export const rowScore = (data: GridRow | undefined, key: ScoreCategory) =>
	data && !isDivider(data) ? data.scores[key].score : null;

export const rowTotal = (data?: GridRow) =>
	data && !isDivider(data) ? data.scores.total.score : null;

export const rowTotalDelta = (data?: GridRow) =>
	data && !isDivider(data) ? data.scores.total.delta : 0;

export const centeredCell = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
} as const;

export const leftCell = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
} as const;

const EmptyPlaceholder = () => <Box style={{ color: tokens.text.secondary, fontSize: 14 }}>-</Box>;

// Printed raw: toDisplayScore would clamp the sentinel to 1 and make a failed
// calculation read as a real (bad) score.
const FailedScorePlaceholder = () => (
	<Box style={{ color: defaultTheme.colors.red, fontSize: 14, fontWeight: 600 }}>
		{FAILED_SCORE}
	</Box>
);

const ScoreCell = ({
	score,
	state,
	getScoreColors,
}: {
	score: number | null;
	state: ScoreModuleState;
	getScoreColors: (score: number) => ScoreColors;
}) => {
	if (score === null) return <EmptyPlaceholder />;

	if (isFailedScore(score)) return <FailedScorePlaceholder />;

	if (state === ScoreModuleState.Unavailable) {
		return (
			<DataCard background="#F1F3F5">
				<Box style={{ width: 12, height: 2, borderRadius: 1, background: '#C2CAD2' }} />
			</DataCard>
		);
	}

	const technicalScore = state === ScoreModuleState.Disabled ? 0 : score;
	const displayScore = toDisplayScore(technicalScore);
	const colors = getScoreColors(displayScore);

	return (
		<DataCard background={colors.background}>
			<NumberFlow value={displayScore} format={{ maximumFractionDigits: 1 }} locales="en-US" />
		</DataCard>
	);
};

export function DividerFullWidthRow({
	api,
	data,
	getScoreSort,
	edgeCount,
}: CustomCellRendererProps<GridRow> & {
	getScoreSort: (columnState: ColumnState[]) => ScoreSort | null;
	edgeCount: number;
}) {
	const { t } = useTranslation();
	const scoreSort = getScoreSort(api.getColumnState());
	if (!scoreSort) return null;

	const isTopSlot = data?.id === TOP_DIVIDER_ID;
	const isTopFive = scoreSort.direction === 'desc' ? isTopSlot : !isTopSlot;

	const position = t(
		isTopFive
			? 'analytics.treamsScore.table.dividerTop'
			: 'analytics.treamsScore.table.dividerBottom',
	);
	const label = `${position} ${edgeCount} · ${scoreSort.label}`;
	const arrow = isTopSlot ? '↑' : '↓';

	return (
		<Flex
			alignItems="center"
			justifyContent="center"
			width="100%"
			height="100%"
			style={{ gap: 12, color: tokens.text.secondary, fontWeight: 600 }}
		>
			<span>{arrow}</span>
			<span>{label}</span>
			<span>{arrow}</span>
		</Flex>
	);
}

export function HiddenTeamsDividerRow({
	api,
	getScoreSort,
	hiddenCount,
}: CustomCellRendererProps<GridRow> & {
	getScoreSort: (columnState: ColumnState[]) => ScoreSort | null;
	hiddenCount: number;
}) {
	const { t } = useTranslation();
	if (!getScoreSort(api.getColumnState())) return null;

	return (
		<Flex
			alignItems="center"
			justifyContent="center"
			width="100%"
			height="100%"
			style={{ gap: 12, color: tokens.text.secondary, fontWeight: 600 }}
		>
			<span>·</span>
			<span>{t('analytics.treamsScore.table.hiddenTeams', { count: hiddenCount })}</span>
			<span>·</span>
		</Flex>
	);
}

export function NameCell({ data }: CustomCellRendererProps<GridRow>) {
	const { t } = useTranslation();

	if (!data || isDivider(data)) return null;

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: 1,
				lineHeight: 1.5,
				minWidth: 0,
				width: '100%',
			}}
		>
			<Ellipsis
				minWidth={0}
				width="100%"
				title={data.name}
				style={{ fontWeight: 400, color: tokens.text.primary }}
			>
				{data.name}
			</Ellipsis>
			<div>{t('analytics.treamsScore.table.people', { count: data.people })}</div>
		</div>
	);
}

export function ManagerCell({ data }: CustomCellRendererProps<GridRow>) {
	if (!data || isDivider(data) || data.managers.length === 0) return <EmptyPlaceholder />;

	return <TreamsScoreManagers managers={data.managers} />;
}

export type ScoreColumnCellProps = CustomCellRendererProps<GridRow> & {
	scoreKey: ScoreCategory;
	state: ScoreModuleState;
	getScoreColors: (score: number) => ScoreColors;
};

export function ScoreColumnCell({ data, scoreKey, state, getScoreColors }: ScoreColumnCellProps) {
	return (
		<ScoreCell score={rowScore(data, scoreKey)} state={state} getScoreColors={getScoreColors} />
	);
}

export type TotalScoreColumnCellProps = CustomCellRendererProps<GridRow>;

// All possible gauge band labels; rendered invisibly in every cell so each
// row's gauge column is as wide as the widest label in the current locale.
const GAUGE_BAND_KEYS = [
	'gauge.band.highPerforming',
	'gauge.band.performing',
	'gauge.band.developing',
	'gauge.band.underperforming',
	'gauge.band.critical',
] as const;

export function TotalScoreColumnCell({ data }: TotalScoreColumnCellProps) {
	const { t } = useTranslation();
	const total = rowTotal(data);
	if (total === null) return <EmptyPlaceholder />;

	if (isFailedScore(total)) return <FailedScorePlaceholder />;

	const delta = rowTotalDelta(data);
	const displayTotal = toDisplayScore(total);

	return (
		<Flex alignItems="flex-start" justifyContent="start" style={{ gap: 14, width: '100%' }}>
			<Box style={{ display: 'inline-grid', justifyItems: 'center' }}>
				<Box style={{ gridArea: '1 / 1' }}>
					<GaugeMeter
						size={57}
						value={displayTotal}
						delta={delta}
						label={t(scoreTranslation(displayTotal))}
						compact={true}
						format={{ maximumFractionDigits: 1 }}
					/>
				</Box>
				{GAUGE_BAND_KEYS.map((key) => (
					<Box
						key={key}
						aria-hidden
						style={{
							gridArea: '1 / 1',
							visibility: 'hidden',
							height: 0,
							overflow: 'hidden',
							fontSize: 12,
							whiteSpace: 'nowrap',
						}}
					>
						{t(key)}
					</Box>
				))}
			</Box>
			<Box mt={2}>
				<Delta
					currentValue={displayTotal}
					previousValue={displayTotal - delta}
					unit="numeric"
					animate
					style={{
						borderRadius: 100,
						transform: 'translateY(1px)',
					}}
				/>
			</Box>
		</Flex>
	);
}
